import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from '../button/button';

export interface DisclosureProps {
  /** Trigger label (already translated by the caller). */
  label: React.ReactNode;
  /** Initial open state; the component then manages it (uncontrolled). */
  defaultOpen?: boolean;
  /**
   * Keeps the panel open and disables the trigger, e.g. while the panel's
   * content carries validation errors the user must be able to see.
   */
  forceOpen?: boolean;
  className?: string;
  panelClassName?: string;
  children: React.ReactNode;
}

/**
 * A ghost-button trigger that shows / hides a panel of optional content.
 * The panel is only mounted while open, so collapsed content never
 * participates in focus order or form queries.
 */
export function Disclosure({
  label,
  defaultOpen = false,
  forceOpen = false,
  className,
  panelClassName,
  children,
}: DisclosureProps): React.JSX.Element {
  const [open, setOpen] = React.useState(defaultOpen);
  const panelId = React.useId();
  const expanded = forceOpen || open;
  return (
    <div className={className}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
        disabled={forceOpen}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'size-4 transition-transform',
            expanded && 'rotate-180',
          )}
        />
        {label}
      </Button>
      {expanded && (
        <div id={panelId} className={panelClassName}>
          {children}
        </div>
      )}
    </div>
  );
}
